import React, { useEffect, useState } from 'react';
import { apiRequest } from '../../../service/apiRequest';
import {
  Table,
  Button,
  Flex,
  Breadcrumb,
  Form,
  message,
  Popconfirm,
  Space,
  Pagination,
  Switch,
  TableProps,
} from 'antd';
import { createStyles } from 'antd-style';
import { HomeOutlined, DeleteOutlined, EditOutlined } from '@ant-design/icons';
import { theme } from 'antd';
import CategoryModal from './CategoryModal';
import { showSuccess, showError } from '../../../components/errorHandler';
import type { PopconfirmProps, TableColumnsType } from 'antd';
import { useCurrentModule } from '../../../hooks/useCurrentModule';

const useStyle = createStyles(({ css }) => ({
  customTable: css`
    .ant-table {
      &-container {
        .ant-table-body,
        .ant-table-content {
          scrollbar-width: thin;
          scrollbar-color: #eaeaea transparent;
        }
      }
    }
  `,
}));

interface Categories {
  category_id: number;
  category_name: string;
  category_parent: number;
  parent_name?: string;
  category_short_code: string;
  category_status: number;
}

const CategoryMaster: React.FC = () => {
  const [categories, setCategories] = useState<Categories[]>([]);
  const [form] = Form.useForm();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [initialValues, setInitialValues] = useState<Categories | null>(null);
  const { styles } = useStyle();
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });
  const currentModule = useCurrentModule();

  const [filteredInfo, setFilteredInfo] = useState<Record<string, any>>({});
  const [sortedInfo, setSortedInfo] = useState<any>({});


  const cancel: PopconfirmProps['onCancel'] = (e) => {
    console.log(e);
    showError('Click on No');
  };

  const {
    token: { colorBgContainer, borderRadiusLG },
  } = theme.useToken();

  const fetchCategory = async () => {
    try {
      const data = await apiRequest<Categories[]>({
        method: 'get',
        url: 'category',
      });
      setCategories(data);
    } catch (error) {
      showError('Failed to fetch categories');
      console.error(error);
    }
  };

  useEffect(() => {
    fetchCategory();
  }, []);

  const handleEditClick = (record: Categories) => {
    setIsEditMode(true);
    setInitialValues({
      category_id: record.category_id,
      category_name: record.category_name,
      category_short_code: record.category_short_code,
      category_parent: record.category_parent,
      category_status: record.category_status,
    });
    setIsModalVisible(true);
  };

  const handleCreate = () => {
    fetchCategory();
  };

  const showCreateModal = () => {
    setIsEditMode(false);
    setInitialValues(null);
    setIsModalVisible(true);
  };

  const hideCreateModal = () => {
    setIsModalVisible(false);
    setIsEditMode(false);
    setInitialValues(null);
  };

  const handleDelete = async (category_id: number) => {
    try {
      await apiRequest({
        method: 'delete',
        url: `/delete-category/${category_id}`,
      });
      showSuccess('Category deleted');
      fetchCategory();
    } catch {
      showError('Failed to delete category');
    }
  };

  const handleStatusToggle = async (categoryId: number) => {
    setCategories((prev) =>
      prev.map((item) =>
        item.category_id === categoryId
          ? { ...item, category_status: item.category_status === 1 ? 0 : 1 }
          : item
      )
    );

    try {
      await apiRequest({
        method: 'put',
        url: `/update-status-category/${categoryId}`,
      });
      showSuccess('Category status updated');
    } catch (error) {
      showError('Failed to update status');
      setCategories((prev) =>
        prev.map((item) =>
          item.category_id === categoryId
            ? { ...item, category_status: item.category_status === 1 ? 0 : 1 }
            : item
        )
      );
    }
  };

  const handleTableChange: TableProps<Categories>['onChange'] = (pagination, filters, sorter) => {
    setFilteredInfo(filters);
    setSortedInfo(sorter);
  };




  const columns: TableColumnsType<Categories> = [
    {
      title: 'Category Parent',
      dataIndex: 'parent_name',
      key: 'parent_name',
      filters: Array.from(
        new Set(categories.map((c) => (c.parent_name ? c.parent_name : 'Root')))
      ).map((name) => ({
        text: name,
        value: name,
      })),
      filterSearch: true,
      filterMode: 'tree',
      filteredValue: filteredInfo.parent_name || null,
      onFilter: (value, record) => (record.parent_name || 'Root').includes(value as string),
      sorter: (a, b) => (a.parent_name || 'Root').localeCompare(b.parent_name || 'Root'),
      sortOrder: sortedInfo.columnKey === 'parent_name' ? sortedInfo.order : null,
      sortDirections: ['ascend', 'descend'],
      ellipsis: true,
      render: (_: string, record: Categories) => (
        <div>{record.parent_name ? record.parent_name : <span style={{ color: '#888' }}>Root</span>}</div>
      ),
    },
    {
      title: 'Category Main',
      key: 'category_main',
      filters: Array.from(
        new Set(
          categories.map((c) =>
            !c.category_parent || c.category_parent === 0 || !c.parent_name
              ? c.category_name
              : c.parent_name
          )
        )
      ).map((name) => ({
        text: name,
        value: name,
      })),
      filterSearch: true,
      filterMode: 'tree',
      filteredValue: filteredInfo.category_main || null,
      onFilter: (value, record) => {
        const isRoot = !record.category_parent || record.category_parent === 0 || !record.parent_name;
        const mainName = isRoot ? record.category_name : record.parent_name;
        return (mainName || '').toLowerCase().includes((value as string).toLowerCase());
      },
      sorter: (a, b) => {
        const mainA = !a.category_parent || a.category_parent === 0 || !a.parent_name ? a.category_name : a.parent_name;
        const mainB = !b.category_parent || b.category_parent === 0 || !b.parent_name ? b.category_name : b.parent_name;
        return (mainA || '').localeCompare(mainB || '');
      },
      sortOrder: sortedInfo.columnKey === 'category_main' ? sortedInfo.order : null,
      sortDirections: ['ascend', 'descend'],
      ellipsis: true,
      render: (_: string, record: Categories) => {
        const isRoot = !record.category_parent || record.category_parent === 0 || !record.parent_name;
        const mainName = isRoot ? record.category_name : record.parent_name;
        return (
          <div style={{ fontWeight: isRoot ? 600 : 400 }}>
            {mainName || '-'}
          </div>
        );
      },
    },
    {
      title: 'Sub Category',
      key: 'sub_category',
      filters: Array.from(
        new Set(
          categories
            .filter((c) => c.category_parent && c.category_parent !== 0)
            .map((c) => c.category_name)
        )
      ).map((name) => ({
        text: name,
        value: name,
      })),
      filterSearch: true,
      filterMode: 'tree',
      filteredValue: filteredInfo.sub_category || null,
      onFilter: (value, record) => {
        const isSub = Boolean(record.category_parent && record.category_parent !== 0);
        return isSub ? record.category_name.toLowerCase().includes((value as string).toLowerCase()) : false;
      },
      sorter: (a, b) => {
        const subA = a.category_parent && a.category_parent !== 0 ? a.category_name : '';
        const subB = b.category_parent && b.category_parent !== 0 ? b.category_name : '';
        return subA.localeCompare(subB);
      },
      sortOrder: sortedInfo.columnKey === 'sub_category' ? sortedInfo.order : null,
      sortDirections: ['ascend', 'descend'],
      ellipsis: true,
      render: (_: string, record: Categories) => {
        const isSub = Boolean(record.category_parent && record.category_parent !== 0);
        return isSub ? (
          <div>{record.category_name}</div>
        ) : (
          <div style={{ color: '#aaa' }}>-</div>
        );
      },
    },
    {
      title: 'Category Code',
      dataIndex: 'category_short_code',
      key: 'category_short_code',
      render: (_: string, record: Categories) => <div>{record.category_short_code}</div>,
    },
  ];

  // Conditionally add action column
  if (currentModule?.right_edit === 'Y' || currentModule?.right_delete === 'Y') {
    columns.push({
      title: 'Action',
      width: 200,
      render: (_: any, record: Categories) => (
        <Space size="middle">
          {currentModule?.right_edit === 'Y' && (
            <>
              <Switch
                checkedChildren="Active"
                unCheckedChildren="Inactive"
                checked={!!record.category_status}
                onChange={() => handleStatusToggle(record.category_id)}
              />
              <Button onClick={() => handleEditClick(record)} className="px-8">
                <EditOutlined />
              </Button>
            </>
          )}
          {currentModule?.right_delete === 'Y' && (
            <Popconfirm
              title="Delete this category?"
              onConfirm={() => handleDelete(record.category_id)}
              onCancel={cancel}
              okText="Yes"
              cancelText="No"
            >
              <Button danger className="px-8">
                <DeleteOutlined />
              </Button>
            </Popconfirm>
          )}
        </Space>
      ),
    });
  }


  return (
    <>
      <Space
        style={{
          display: 'flex',
          marginBottom: 16,
          alignItems: 'center',
          width: '100%',
          justifyContent: 'space-between',
        }}
      >
        <Breadcrumb style={{ display: 'inline-block', margin: 0 }}>
          <Breadcrumb.Item href="/">
            <HomeOutlined />
          </Breadcrumb.Item>
          <Breadcrumb.Item>View Categories</Breadcrumb.Item>
        </Breadcrumb>

        {currentModule?.right_add === 'Y' && (
          <Flex gap="small">
            <Button type="primary" onClick={showCreateModal}>
              Add Category
            </Button>
          </Flex>
        )}
      </Space>


      <div
        style={{
          minHeight: 360,
          background: colorBgContainer,
          borderRadius: borderRadiusLG,
        }}
      >
        <Table<Categories>
          tableLayout="auto"
          className={`${styles.customTable} scrollable-table`}
          columns={columns}
          rowKey="category_id"
          onChange={handleTableChange}
          dataSource={categories.slice(
            (pagination.current - 1) * pagination.pageSize,
            pagination.current * pagination.pageSize
          )}
          bordered
          pagination={false}
          scroll={{ y: 37 * 13 }}
          locale={{ emptyText: 'No category found' }}
          footer={() => (
            <Flex justify="space-between">
              <span>
                Showing {(pagination.current - 1) * pagination.pageSize + 1}–
                {Math.min(pagination.current * pagination.pageSize, categories.length)} of{' '}
                {categories.length}
              </span>
              <Pagination
                {...pagination}
                total={categories.length}
                showSizeChanger
                onChange={(page, pageSize) => setPagination({ current: page, pageSize })}
              />
            </Flex>
          )}
        />
      </div>

      <CategoryModal
        visible={isModalVisible}
        onCancel={hideCreateModal}
        onCreate={handleCreate}
        isEditMode={isEditMode}
        initialValues={initialValues}
      />
    </>
  );
};

export default CategoryMaster;
